/**
 * ReaderStatsCard.jsx — Reader version of the WriterStatsCard.
 *
 * Evolution tier + level header, XP bar painted the tier hex, coin balance
 * (reader-coins, shop-only — no £ conversion unlike writer-coins), and a
 * 5-stat grid: WPM, Comprehension %, Daily Streak, Books Completed, Track
 * Mastery. Intentionally mirror-shaped to the WriterStatsCard so users who
 * are both readers AND scribes feel the parity.
 */
import React, { useEffect, useState } from 'react';
import {
  Sparkles, Coins, Gauge, Brain, Flame, BookOpen, Target, TrendingUp,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_GLOW = {
  'Curious Reader':     'from-slate-400/25 via-slate-500/10',
  'Steady Explorer':    'from-amber-700/25 via-amber-800/10',
  'Bookworm':           'from-orange-600/25 via-orange-700/10',
  'Page Master':        'from-yellow-400/25 via-yellow-500/10',
  'Literary Adept':     'from-emerald-500/25 via-emerald-600/10',
  'Story Sage':         'from-blue-500/25 via-blue-600/10',
  'Deep Reader':        'from-red-500/25 via-red-600/10',
  'Scholar':            'from-purple-500/25 via-purple-600/10',
  'Literary Luminary':  'from-cyan-400/25 via-cyan-500/10',
  'Omnireader':         'from-gray-200/25 via-gray-300/10',
};

const StatTile = ({ icon: Icon, label, value, sub, colour = 'text-white', testId }) => (
  <div className="p-3 bg-slate-900/40 rounded-lg border border-slate-700/50" data-testid={testId}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-400 mb-1">
      <Icon className="w-3.5 h-3.5" /> {label}
    </div>
    <div className={`text-xl font-bold ${colour}`}>{value}</div>
    {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

const ReaderStatsCard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/api/reader/stats`);
        if (r.ok) setStats(await r.json());
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <Card className="bg-slate-800/50 border-slate-700" data-testid="reader-stats-card">
        <CardContent className="p-6 text-gray-400 text-sm">Loading reader stats…</CardContent>
      </Card>
    );
  }
  if (!stats) return null;

  const tier = stats.tierMeta || { title: stats.readerEvolutionTier, hex: '#9CA3AF' };
  const glow = TIER_GLOW[tier.title] || TIER_GLOW['Curious Reader'];
  const xpInLevel = stats.readerXPInCurrentLevel || 0;
  const thresholds = [800, 1500, 2500, 4000, 6000, 8000, 10000, 13000, 17000, 22000];
  const xpThreshold = stats.readerLevel >= 100 ? 22000 : thresholds[Math.floor((Math.max(1, stats.readerLevel) - 1) / 10)];
  const pct = Math.min(100, Math.round((xpInLevel / xpThreshold) * 100));
  const cs = stats.coreStats || {};

  return (
    <Card className="bg-slate-800/50 border-slate-700 overflow-hidden" data-testid="reader-stats-card">
      <div className={`relative p-5 bg-gradient-to-br ${glow} to-transparent border-b border-slate-700/50`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
                style={{ backgroundColor: `${tier.hex}22`, color: tier.hex, border: `1px solid ${tier.hex}55` }}
                data-testid="reader-tier-badge"
              >
                <Sparkles className="w-3 h-3" /> {tier.title}
              </span>
              <span className="text-[11px] text-gray-400">Levels {tier.min}–{tier.max}</span>
            </div>
            <h3 className="text-2xl font-bold text-white">
              Level {stats.readerLevel}
              <span className="text-sm text-gray-400 ml-2 font-normal">
                {stats.readerTotalXP.toLocaleString()} lifetime XP
              </span>
            </h3>
          </div>
          <div className="text-right" data-testid="reader-coin-balance">
            <div className="flex items-center justify-end gap-1.5 text-sky-300">
              <Coins className="w-5 h-5" />
              <span className="text-2xl font-extrabold">{(stats.coinBalance || 0).toLocaleString()}</span>
            </div>
            <div className="text-[11px] text-gray-400">shop coins</div>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex items-center justify-between text-[11px] text-gray-400 mb-1">
            <span>XP to next level</span>
            <span>{xpInLevel.toLocaleString()} / {xpThreshold.toLocaleString()}</span>
          </div>
          <div className="h-2 bg-slate-900/60 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${pct}%`, backgroundColor: tier.hex }}
              data-testid="reader-xp-bar"
            />
          </div>
        </div>
      </div>

      <CardHeader className="pb-2">
        <CardTitle className="text-white text-base flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-indigo-400" /> Core Stats
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="reader-core-stats">
        <StatTile
          icon={Gauge}
          label="Reading Speed"
          value={`${cs.wpm || 0} WPM`}
          sub={`avg ${cs.wpmAvg || 0}`}
          colour="text-emerald-300"
          testId="reader-stat-wpm"
        />
        <StatTile
          icon={Brain}
          label="Comprehension"
          value={`${(cs.comprehensionRate || 0).toFixed(0)}%`}
          sub="rolling 10-lesson avg"
          colour="text-sky-300"
          testId="reader-stat-comprehension"
        />
        <StatTile
          icon={Flame}
          label="Daily Streak"
          value={cs.dailyConsistency || 0}
          sub={`best: ${cs.bestStreak || 0}`}
          colour="text-amber-300"
          testId="reader-stat-streak"
        />
        <StatTile
          icon={BookOpen}
          label="Books Completed"
          value={cs.booksCompleted || 0}
          sub=""
          colour="text-purple-300"
          testId="reader-stat-books"
        />
        <StatTile
          icon={Target}
          label="Track Mastery"
          value={`${cs.trackMastery || 0}/100`}
          sub="weighted avg"
          colour="text-fuchsia-300"
          testId="reader-stat-mastery"
        />
      </CardContent>

      {stats.activityLog && stats.activityLog.length > 0 && (
        <div className="px-5 pb-5" data-testid="reader-activity-log">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-gray-400 mb-2">
            <TrendingUp className="w-3.5 h-3.5" /> Recent activity
          </div>
          <ul className="space-y-1">
            {stats.activityLog.slice(0, 5).map((e, i) => (
              <li
                key={`${e.eventKey}-${i}`}
                className="flex items-center justify-between gap-3 text-xs p-2 bg-slate-900/30 rounded-md border border-slate-800"
              >
                <span className="text-gray-200 truncate">{e.description || e.eventKey}</span>
                <span className="shrink-0 text-sky-300 font-semibold">
                  +{e.xpEarned} XP · +{e.coinsEarned}c
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
};

export default ReaderStatsCard;
